import { useMemo } from 'react';
import { useAuth } from '../contexts/Auth/authContext';

/**

 *
 * | ANEP        | clients | fleet writes | accounts       | audit | settings |
 * |-------------|---------|--------------|----------------|-------|----------|
 * | SuperAdmin  | yes     | yes          | read + write   | yes   | yes      |
 * | Admin       | yes     | yes          | read only      | yes   | no       |
 * | Operator    | no      | yes          | no             | no    | no       |
 *
 * | Client      | own devices | own accounts |
 * |-------------|-------------|--------------|
 * | ClientAdmin | yes         | read + write |
 * | Viewer      | read only   | no           |
 *
 /** */
 
export const useClientAccess = () => {
  const { user, serverScope } = useAuth();

  return useMemo(() => {
    const roles = user?.roles ?? [];
    const isSuperAdmin = roles.includes('SuperAdmin');
    const isAdmin = roles.includes('Admin');
    const isOperator = roles.includes('Operator');
    const isClientAdmin = roles.includes('ClientAdmin');
    const isViewer = roles.includes('Viewer');

    // The realm always adds default-roles-*, offline_access and
    // uma_authorization, so `roles.length` is never a usable test.
    const hasApplicationRole = isSuperAdmin || isAdmin || isOperator || isClientAdmin || isViewer;

    const isAnepStaff = isSuperAdmin || isAdmin || isOperator || (user?.isAnepStaff ?? false);
    // à voir 
    // isanepmanager est un membre du personnel ANEP qui a le rôle de SuperAdmin ou Admin, donc il peut gérer les clients et voir le journal d'audit.
    const isAnepManager = isAnepStaff && (isSuperAdmin || isAdmin);
    const isAnepOperator = isAnepStaff && isOperator;

    const isClientStaff = !isAnepStaff && isClientAdmin;

    // What the server says this session may reach, once it has answered;
    // the token's own reading until then.
    const allowedClientIds = serverScope ? serverScope.clientIds : (user?.clientIds ?? []);
    const scopeResolved = serverScope != null;
    const hasSingleClient = allowedClientIds.length === 1;

    const hasOnlyViewerRole = isViewer && !isSuperAdmin && !isAdmin && !isOperator && !isClientAdmin;

    // Accounts: read for SuperAdmin, Admin and a client's own admin; write
    // for SuperAdmin and ClientAdmin only. Mirrors USER_MANAGEMENT_ROLES /
    // USER_WRITE_ROLES on the API.
    const canManageUsers = isSuperAdmin || isAdmin || isClientAdmin;
    const canWriteUsers = isSuperAdmin || isClientAdmin;

    // The catalogue — device types, firmware — is ANEP-side and open to
    // every ANEP role for writing. Mirrors FLEET_WRITE_ROLES.
    const canWriteCatalog = isSuperAdmin || isAnepManager || isAnepOperator;

    // Configuration templates are for everyone: ANEP roles write any
    // template, a ClientAdmin writes their own client's, a Viewer reads.
    // Bulk assignment (a template onto clients or devices): SuperAdmin on
    // the ANEP side, a client's admin on their own templates.
    const canWriteTemplates = canWriteCatalog || isClientStaff;
    const canAssignTemplates = isSuperAdmin || isClientStaff;

    return {
      isSuperAdmin,
      isAdmin,
      isOperator,
      isClientAdmin,
      isViewerOnly: hasOnlyViewerRole,
      isAnepStaff,
      isAnepManager,
      isClientStaff,
      hasApplicationRole,

      // Clients and the audit log belong to those who run the platform.
      canManageClients: isAnepManager,
      canViewAuditLog: isAnepManager,
      shouldShowClientUI: isAnepStaff,

      canAccessCatalog: isAnepStaff,
      canAccessOtaUpdates: isAnepStaff,
      canManageFirmware: canWriteCatalog,
      canEditDeviceTypes: canWriteCatalog,

      canAccessTemplates: hasApplicationRole,
      canWriteTemplates,
      canAssignTemplates,

      canManageUsers,
      canWriteUsers,

      canManageDevices:
        isSuperAdmin ||
        isAnepManager ||
        isAnepOperator ||
        (isClientAdmin && allowedClientIds.length > 0),

      // Shut out: no application role at all on this environment, or a
      // client-side session whose scope came back empty — never granted,
      // revoked, or the whole client suspended. A `null` scope is "not
      // answered yet" and must not lock anyone out.
      isLockedOut:
        !hasApplicationRole ||
        (scopeResolved && !isAnepStaff && allowedClientIds.length === 0),

      allowedClientIds,
      hasFullAccess: isAnepStaff,
      hasSingleClient,
      hasAnyClientAccess: isAnepStaff || allowedClientIds.length > 0,

      canAccessClient: (clientId: string): boolean => {
        if (isAnepStaff) return true;
        return allowedClientIds.includes(clientId);
      },

      filterByClientAccess: <T extends { client_id?: string | null }>(items: T[]): T[] => {
        if (isAnepStaff) return items;
        return items.filter(item => {
          if (!item.client_id) return true;
          return allowedClientIds.includes(item.client_id);
        });
      },
    };
  }, [user?.roles, user?.clientIds, user?.isAnepStaff, serverScope]);
};

export default useClientAccess;
